import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ScoreDisplay from './quiz-results/ScoreDisplay';
import PerformanceSummary from './quiz-results/PerformanceSummary';
import PerformanceComparison from './quiz-results/PerformanceComparison';
import QuestionReview from './quiz-results/QuestionReview';
import { useQuizResults } from './quiz-results/useQuizResults';
import { getScoreColor, getScoreBadge, getPerformanceMessage, getQuestionById } from './quiz-results/quizResultsUtils';
import '../styles/Quiz.css';

const QuizResults = ({ results: propResults, quiz: propQuiz, onReturnToQuizzes }) => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('summary');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Use custom hook for data fetching
  const { quiz, results, loading, error } = useQuizResults(quizId, propResults, propQuiz);

  useEffect(() => {
    document.body.classList.add("quiz-body");
    return () => {
      document.body.classList.remove("quiz-body");
    };
  }, []);

  if (loading) {
    return (
      <div className="quiz-state-container">
        <div className="spinner-modern"></div>
        <p className="quiz-state-text">Loading results...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-container-modern">
        <div className="quiz-state-container">
          <div className="alert-modern alert-danger-modern">
            <h4>Error</h4>
            <p>{error}</p>
            <button
              className="btn-modern btn-secondary-modern mt-3"
              onClick={() => navigate('/quizzes')}
            >
              Back to Quizzes
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!quiz || !results) {
    return (
      <div className="quiz-container-modern">
        <div className="alert-modern alert-warning-modern">No results found for this quiz.</div>
        <button
          className="btn-modern btn-primary-modern mt-3"
          onClick={() => navigate('/quizzes')}
        >
          Back to Quizzes
        </button>
      </div>
    );
  }

  return (
    <div className="quiz-container-modern">
      <div className="quiz-content-wrapper">
        <div className="quiz-results-container">
          {/* Header Section with Score and Quick Stats */}
          <div className="quiz-card mb-3">
            <div className="quiz-card-header text-center">
              <h4 className="quiz-title-modern">Quiz Results</h4>
              <p className="quiz-description-modern">{quiz?.title}</p>
            </div>
            <div className="quiz-card-body">
              <div className="row align-items-center">
                <ScoreDisplay 
                  results={results} 
                  getScoreColor={getScoreColor} 
                  getScoreBadge={getScoreBadge} 
                />
                <PerformanceSummary 
                  results={results} 
                  getPerformanceMessage={getPerformanceMessage} 
                />
                <PerformanceComparison results={results} quiz={quiz} />
              </div>
            </div>
          </div>

          {/* Tabbed Interface */}
          <div className="quiz-card mb-2">
            <div className="quiz-card-header">
              <div className="quiz-tabs">
                <button 
                  className={`quiz-tab ${activeTab === 'summary' ? 'active' : ''}`}
                  onClick={() => setActiveTab('summary')}
                >
                  Summary & Analysis
                </button>
                <button 
                  className={`quiz-tab ${activeTab === 'review' ? 'active' : ''}`}
                  onClick={() => setActiveTab('review')}
                >
                  Question Review
                </button>
                <button 
                  className={`quiz-tab ${activeTab === 'share' ? 'active' : ''}`}
                  onClick={() => setActiveTab('share')}
                >
                  Share
                </button>
              </div>
            </div>
            <div className="quiz-card-body">
              {activeTab === 'review' && (
                <QuestionReview 
                  quiz={quiz}
                  results={results}
                  currentQuestionIndex={currentQuestionIndex}
                  setCurrentQuestionIndex={setCurrentQuestionIndex}
                  getQuestionById={(questionId) => getQuestionById(quiz, questionId)}
                />
              )}
              
              {activeTab === 'summary' && (
                <div className="summary-section">
                  {/* Pattern Analysis */}
                  {quiz?.questions && (
                    <div className="mb-4">
                      <h5 className="mb-3 text-light">Pattern Analysis</h5>
                      {(() => {
                        const patternStats = {};
                        quiz.questions.forEach(question => {
                          question.patterns_tested?.forEach(pattern => {
                            if (!patternStats[pattern]) {
                              patternStats[pattern] = { total: 0, correct: 0 };
                            }
                            patternStats[pattern].total++;
                            if (results.feedback[question.question_id]?.correct) {
                              patternStats[pattern].correct++;
                            }
                          });
                        });

                        return (
                          <div className="row">
                            {Object.entries(patternStats).map(([pattern, stats]) => {
                              const percentage = (stats.correct / stats.total) * 100;
                              return (
                                <div key={pattern} className="col-md-6 mb-3">
                                  <div className="quiz-card">
                                    <div className="quiz-card-body">
                                      <h6 className="text-light text-capitalize">
                                        {pattern.replace('_', ' ')}
                                      </h6>
                                      <div className="progress-container mb-2">
                                        <div 
                                          className="progress-bar-modern"
                                          style={{ width: `${percentage}%` }}
                                        >
                                          {Math.round(percentage)}%
                                        </div>
                                      </div>
                                      <small className="quiz-description-modern">
                                        {stats.correct}/{stats.total} questions correct
                                      </small>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'share' && (
                <div className="share-section-quiz-results">
                  <div className="share-card-quiz-results" id="share-card">
                    <div className="share-card-inner-qr">
                      <div className="share-card-logo-qr">Prep<span>Letter</span></div>
                      <div className="share-card-tagline-qr">Daily Interview Prep</div>
                      <div className="share-card-divider-qr"></div>
                      <div className="share-card-quiz-qr">
                        {quiz?.title || `Letter #${quiz?.letter_id} Quiz`}
                      </div>
                      <div className="share-card-score-qr">{Math.round(results.score)}%</div>
                      <div className="share-card-detail-qr">
                        {results.correct_answers}/{results.total_questions} correct
                      </div>
                      <div className="share-card-divider-qr"></div>
                      <div className="share-card-footer-qr">
                        <span className="share-card-url-qr">prepletter.app</span>
                      </div>
                    </div>
                  </div>
                  <div className="share-buttons-qr">
                    <button
                      className="share-btn-qr share-linkedin-qr"
                      onClick={() => {
                        const title = quiz?.title || '';
                        const score = Math.round(results.score);
                        const emoji = score >= 80 ? '🟢' : score >= 60 ? '🟡' : '🔴';
                        const text = encodeURIComponent(
                          `${emoji} PrepLetter Quiz Result\n━━━━━━━━━━━━━━━━━━\n📚 ${title}\n🎯 Score: ${score}% (${results.correct_answers}/${results.total_questions} correct)\n━━━━━━━━━━━━━━━━━━\nPrepLetter helps you master coding interviews with daily practice letters and interactive quizzes.\n\n💻 Try it free → prepletter.app/learn\n\n#CodingInterview #LeetCode #TechJobs #Programming #SoftwareEngineering`
                        );
                        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=https://prepletter.app/learn&summary=${text}`, '_blank');
                      }}
                    >
                      <i className="bi bi-linkedin"></i> Share on LinkedIn
                    </button>
                    <button
                      className="share-btn-qr share-copy-qr"
                      onClick={() => {
                        const title = quiz?.title || '';
                        const score = Math.round(results.score);
                        const emoji = score >= 80 ? '🟢' : score >= 60 ? '🟡' : '🔴';
                        const text = `${emoji} PrepLetter Quiz Result\n━━━━━━━━━━━━━━━━━━\n📚 ${title}\n🎯 Score: ${score}% (${results.correct_answers}/${results.total_questions} correct)\n━━━━━━━━━━━━━━━━━━\n💻 Try it free → prepletter.app/learn`;
                        navigator.clipboard.writeText(text);
                        alert('Copied to clipboard!');
                      }}
                    >
                      <i className="bi bi-clipboard"></i> Copy Link
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="quiz-card">
            <div className="quiz-card-body text-center">
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <button 
                  className="btn-modern btn-primary-modern"
                  onClick={onReturnToQuizzes || (() => navigate('/quizzes'))}
                  style={{ minWidth: '160px' }}
                >
                  <i className="bi bi-arrow-left"></i> Back to Quizzes
                </button>
                
                {/* Letter Review Button for Daily Quizzes */}
                {quiz?.quiz_type === 'daily_letter' && quiz?.letter_id && (
                  <button 
                    className="btn-modern btn-info-modern"
                    onClick={() => navigate(`/prepletters?letter=${quiz.letter_id}`)}
                    style={{ minWidth: '160px' }}
                  >
                    <i className="bi bi-envelope-open"></i> Review Letter #{quiz.letter_id}
                  </button>
                )}
              </div>
              
              {results.score < 70 && (
                <div className="mt-3">
                  <div className="alert-modern alert-warning-modern">
                    <strong className="text-light">Recommendation:</strong> Consider reviewing the material and retaking this quiz to improve your understanding.
                    {quiz?.quiz_type === 'daily_letter' && quiz?.letter_id && (
                      <div className="mt-2">
                        <button 
                          className="btn-modern btn-sm btn-outline-light"
                          onClick={() => navigate(`/prepletters?letter=${quiz.letter_id}`)}
                        >
                          Review PrepLetter #{quiz.letter_id} Again
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bug Report Link */}
        <div className="quiz-bug-report mt-4 text-center">
          <a 
            href={`/bug-report?page=quiz-results&entity=quiz-${quiz?.quiz_id || 'unknown'}`} 
            className="bug-report-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            🐛 Report a Bug
          </a>
        </div>
      </div>
    </div>
  );
};

export default QuizResults;
